import {
  AdvancedSearchQuery,
  OPERATOR_REGISTRY,
} from '../components/TableSearch/types';

/**
 * Encodes/decodes an AdvancedSearchQuery to/from a URL-safe base64 string,
 * for carrying a Products advanced-search filter as a shareable deep-link
 * query param (?filter=<encoded>). See docs/features/metrics-deep-linking.md
 * Architecture Decision 1.
 */

// Manual UTF-8 <-> byte conversion, instead of TextEncoder/TextDecoder:
// this project's jsdom test environment has no TextEncoder (see the note in
// client/src/pages/__tests__/LgmProductNavigator.test.tsx), so any module
// importable from a test file must avoid it to stay testable.
function utf8Encode(str: string): Uint8Array {
  const bytes: number[] = [];
  for (let i = 0; i < str.length; i += 1) {
    const code = str.codePointAt(i) as number;
    if (code > 0xffff) {
      i += 1; // consume the second half of the surrogate pair
    }
    if (code < 0x80) {
      bytes.push(code);
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code < 0x10000) {
      bytes.push(
        0xe0 | (code >> 12),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f)
      );
    } else {
      bytes.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f)
      );
    }
  }
  return new Uint8Array(bytes);
}

function utf8Decode(bytes: Uint8Array): string {
  let result = '';
  let i = 0;
  while (i < bytes.length) {
    const byte1 = bytes[i];
    if (byte1 < 0x80) {
      result += String.fromCharCode(byte1);
      i += 1;
    } else if ((byte1 & 0xe0) === 0xc0) {
      result += String.fromCharCode(
        ((byte1 & 0x1f) << 6) | (bytes[i + 1] & 0x3f)
      );
      i += 2;
    } else if ((byte1 & 0xf0) === 0xe0) {
      result += String.fromCharCode(
        ((byte1 & 0x0f) << 12) |
          ((bytes[i + 1] & 0x3f) << 6) |
          (bytes[i + 2] & 0x3f)
      );
      i += 3;
    } else {
      const codepoint =
        ((byte1 & 0x07) << 18) |
        ((bytes[i + 1] & 0x3f) << 12) |
        ((bytes[i + 2] & 0x3f) << 6) |
        (bytes[i + 3] & 0x3f);
      result += String.fromCodePoint(codepoint);
      i += 4;
    }
  }
  return result;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function base64UrlDecode(value: string): Uint8Array {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    '='
  );
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** A decoded rule's `value` survives JSON round-tripping as only string,
 * number, or a 2-element array (isBetween's [start, end]) -- a `Date`
 * instance never does, JSON has no Date type, so SearchRule's `Date`/
 * `[Date, Date]` members only ever arrive here as strings. */
function isValidRuleValue(value: unknown): boolean {
  if (typeof value === 'string' || typeof value === 'number') {
    return true;
  }
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    value.every(item => typeof item === 'string' || typeof item === 'number')
  );
}

/** Validates `operator` against the same registry the advanced-search UI
 * and `advancedSearchBuilder.js` use -- an unrecognized operator otherwise
 * reaches `buildOperatorCondition()` server-side, which throws
 * "Unsupported operator" and surfaces as an API error instead of
 * decodeFilter's documented unfiltered-fallback contract (Copilot review,
 * PR #829 round 4). Field/operator data-type compatibility (e.g.
 * `greaterThan` on a string field) is NOT checked here -- that needs the
 * target page's field config, which isn't available at decode time; left
 * as a known gap rather than guessed at. */
function isValidSearchRule(value: unknown): boolean {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const rule = value as Record<string, unknown>;
  return (
    typeof rule.id === 'string' &&
    typeof rule.field === 'string' &&
    typeof rule.operator === 'string' &&
    Object.prototype.hasOwnProperty.call(OPERATOR_REGISTRY, rule.operator) &&
    isValidRuleValue(rule.value)
  );
}

function isValidFilterGroup(value: unknown): boolean {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const group = value as Record<string, unknown>;
  return (
    typeof group.id === 'string' &&
    (group.logicalOperator === 'AND' || group.logicalOperator === 'OR') &&
    Array.isArray(group.rules) &&
    group.rules.every(isValidSearchRule)
  );
}

/** Validates every level of the decoded shape -- not just the top-level
 * envelope -- so a well-formed-but-incomplete payload (e.g. a filter group
 * with no `rules` array) is rejected here rather than crashing later in
 * AdvancedSearch.normalizeQuery()'s `group.rules.map`, keeping decodeFilter's
 * "fail gracefully" contract intact for every level of a tampered/malformed
 * URL, not just the outermost one. */
function isValidAdvancedSearchQuery(
  value: unknown
): value is AdvancedSearchQuery {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const query = value as AdvancedSearchQuery;
  return (
    Array.isArray(query.filterGroups) &&
    query.filterGroups.every(isValidFilterGroup) &&
    (query.groupOperator === 'AND' || query.groupOperator === 'OR')
  );
}

/** Encodes an AdvancedSearchQuery into a URL-safe base64 string. */
export function encodeFilter(query: AdvancedSearchQuery): string {
  const json = JSON.stringify(query);
  const bytes = utf8Encode(json);
  return base64UrlEncode(bytes);
}

/**
 * Decodes a URL-safe base64 string back into an AdvancedSearchQuery.
 * Returns null on any malformed/unparseable input instead of throwing, so a
 * tampered or stale deep-link URL fails gracefully (unfiltered view) rather
 * than crashing the app on load.
 */
export function decodeFilter(value: string): AdvancedSearchQuery | null {
  try {
    const bytes = base64UrlDecode(value);
    const json = utf8Decode(bytes);
    const parsed: unknown = JSON.parse(json);
    return isValidAdvancedSearchQuery(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
